"use client";

import React, { useEffect, useState } from 'react';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

type Consent = {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
    partnersCount?: number;
};

const STORAGE_KEY = 'cookieConsent';

export default function PrivacyCookieSettings() {
    const { siteLang } = useSiteLanguage();
    const en = siteLang === 'en';
    const [consent, setConsent] = useState<Consent>({ essential: true, analytics: false, marketing: false, partnersCount: 210 });
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                setConsent(JSON.parse(raw));
            }
        } catch (e) { }
    }, []);

    const toggle = (key: keyof Consent) => {
        setConsent(prev => ({ ...prev, [key]: !(prev as any)[key] }));
    };

    const save = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
    };

    return (
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h2 className="text-2xl font-semibold mb-4">{en ? 'Privacy & Cookie Settings' : 'Postavke privatnosti i kolačića'}</h2>
            <p className="mb-4">{en ? 'Customize which cookies and tracking technologies you allow.' : 'Prilagodite koje kolačiće i tehnologije praćenja dozvoljavate.'}</p>

            <div className="space-y-4">
                <label className="flex items-center justify-between">
                    <div>
                        <div className="font-semibold">{en ? 'Essential' : 'Neophodni'}</div>
                        <div className="text-sm text-gray-600">{en ? 'Required for the site to function' : 'Neophodni za rad sajta'}</div>
                    </div>
                    <input type="checkbox" checked readOnly />
                </label>

                <label className="flex items-center justify-between">
                    <div>
                        <div className="font-semibold">{en ? 'Analytics' : 'Analitika'}</div>
                        <div className="text-sm text-gray-600">{en ? 'Measuring usage and performance' : 'Mjerenje upotrebe i performansi'}</div>
                    </div>
                    <input type="checkbox" checked={consent.analytics} onChange={() => toggle('analytics')} />
                </label>

                <label className="flex items-center justify-between">
                    <div>
                        <div className="font-semibold">{en ? 'Marketing' : 'Marketing'}</div>
                        <div className="text-sm text-gray-600">{en ? 'Personalized advertising and content' : 'Personalizovano oglašavanje i sadržaj'}</div>
                    </div>
                    <input type="checkbox" checked={consent.marketing} onChange={() => toggle('marketing')} />
                </label>

                <div className="text-sm text-gray-600">{en
                    ? <>{`Approximately `}<strong>{consent.partnersCount}</strong>{` partners may process your data depending on the choices above. Some providers may rely on legitimate interest. You can object through these settings.`}</>
                    : <>Otprilike <strong>{consent.partnersCount}</strong> partnera može obraditi vaše podatke u zavisnosti od izbora iznad. Neki provajderi se mogu osloniti na legitimni interes. Možete uložiti prigovor putem ovih postavki.</>
                }</div>

                <div className="flex items-center gap-3">
                    <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded">{en ? 'Save settings' : 'Sačuvaj postavke'}</button>
                    {saved && <span className="text-green-600">{en ? 'Saved' : 'Sačuvano'}</span>}
                </div>
            </div>
        </div>
    );
}
